import React from "react";
import { FiX, FiClock, FiUser, FiTag } from "react-icons/fi";
import { CATEGORIES } from "../../utils/constants";

const PostPreview = ({ isOpen, onClose, postData }) => {
  if (!isOpen || !postData) return null;

  const formatDate = (date) => {
    return new Date(date || Date.now()).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const getCategoryDetails = (categoryValue) => {
    return (
      CATEGORIES.find((cat) => cat.value === categoryValue) || {
        label: categoryValue,
        color: "#6b7280",
      }
    );
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20">
        {/* Background overlay */}
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75"
          onClick={onClose}
        ></div>

        {/* Preview panel */}
        <div className="relative bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
          {/* Header */}
          <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">
              Post Preview
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <FiX className="h-6 w-6" />
            </button>
          </div>

          {/* Content */}
          <div className="overflow-y-auto px-6 py-6">
            <article className="max-w-3xl mx-auto">
              {/* Featured Image */}
              {postData.imagePreview && (
                <div className="mb-8">
                  <img
                    src={postData.imagePreview}
                    alt={postData.title}
                    className="w-full h-auto rounded-lg shadow-lg"
                  />
                </div>
              )}

              {/* Title */}
              <h1 className="text-4xl font-bold text-gray-900 mb-4">
                {postData.title || "Untitled Post"}
              </h1>

              {/* Meta Information */}
              <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 mb-6">
                <div className="flex items-center gap-1">
                  <FiUser className="w-4 h-4" />
                  <span>{postData.author || "Unknown Author"}</span>
                </div>
                <div className="flex items-center gap-1">
                  <FiClock className="w-4 h-4" />
                  <span>{formatDate(postData.publishDate)}</span>
                </div>
              </div>

              {/* Categories */}
              {postData.categories && postData.categories.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-6">
                  {postData.categories.map((categoryValue) => {
                    const category = getCategoryDetails(categoryValue);
                    return (
                      <span
                        key={categoryValue}
                        className="px-3 py-1 text-xs font-medium text-white rounded-full"
                        style={{ backgroundColor: category.color }}
                      >
                        {category.label}
                      </span>
                    );
                  })}
                </div>
              )}

              {/* Excerpt */}
              {postData.excerpt && (
                <div className="text-lg text-gray-700 font-medium mb-6 italic">
                  {postData.excerpt}
                </div>
              )}

              {/* Content */}
              <div
                className="prose prose-lg max-w-none"
                dangerouslySetInnerHTML={{
                  __html: postData.content || "<p>No content available</p>",
                }}
              />

              {/* Tags */}
              {postData.tags && postData.tags.length > 0 && (
                <div className="mt-8 pt-6 border-t">
                  <div className="flex items-center gap-2 flex-wrap">
                    <FiTag className="text-gray-500" />
                    {postData.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Display Options */}
              <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                <h3 className="font-semibold text-sm text-gray-700 mb-2">
                  Display Settings
                </h3>
                <div className="grid grid-cols-3 gap-2 text-sm">
                  <div>
                    <span className="text-gray-600">Featured:</span>{" "}
                    <span className="font-medium">
                      {postData.is_featured ? "Yes" : "No"}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600">Trending:</span>{" "}
                    <span className="font-medium">
                      {postData.is_trending ? "Yes" : "No"}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600">In Banner:</span>{" "}
                    <span className="font-medium">
                      {postData.is_in_banner ? "Yes" : "No"}
                    </span>
                  </div>
                </div>
              </div>
            </article>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PostPreview;
